import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { TableFilterList } from 'mui-datatables';
import _, { omit, uniqBy } from 'lodash';
import {
  Checkbox,
  FormControlLabel,
  Box,
  TextField,
  Button,
  TableRow,
  TableCell,
  Typography,
  TableFooter,
  Tooltip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  PlayCircleOutline as PlayCircleOutlineIcon,
  PauseCircleOutline as PauseCircleOutlineIcon,
} from '@mui/icons-material';
import Link from '@mui/material/Link';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { confirmAlert } from 'react-confirm-alert';
import BaseMUIDataTable from './BaseMUIDataTable/BaseMUIDataTable';
import TableActionButton from './TableActionButton';
import TableActionsButtonsWrapper from './TableActionsButtonsWrapper';
import { getMuiTableDataIndex } from '../helpers/getters';
import { formatCsvFileName } from '../helpers/formatters';
import { getRescueEstimatedLbs, getRescuePickupLocationFullName, renderDonationPickupLocationNameOrAdress } from '../helpers/RescuesHelper';
import { foodDonorNameFilterOptions } from '../helpers/MuiTableCustomFilters';
import ConfirmationDialog from './ConfirmationDialog';
import { hasAnyRoleInCurrentlySelectedSite } from '../services/auth';
import useActiveUser from '../hooks/useActiveUser';
import { Roles } from '../models/roles';
import UndoButton from './UndoButton';
import { Colors } from '../assets/theme/Colors';
import useSystemSettings from '../hooks/useSystemSettings';
import { LBS_PER_MEAL } from '../models/systemSettings';
import { sortAlphabetically } from '../helpers/sorters';
import DonorLinkIcon from './DonorLinkIcon';
import useActiveSite from '../hooks/useActiveSite';
import { fetchSiteGroupsIfNeeded } from '../actions/groups';
import { fetchSiteDonorsLocationsIfNeeded } from '../actions/sites';
import { DONOR_LINK_PAUSED_TOOLTIP, isDonationPausedByDonorLink } from '../helpers/donations';

const CustomFilterList = ({ filterList, filterUpdate, ...restProps }, editMode) => {
  const { options, columnNames } = restProps;
  const {getDonationFoodDonorType, getDonationPickupLocationType, onFoodDonorFilterChange, onPickupLocationFilterChange} = options.customFilters;

  const verificationStatusColumnIndex = columnNames.findIndex(col => col.name === 'verification_status');
  const verificationStatusColumn = columnNames[verificationStatusColumnIndex];

  const updatedList = [...filterList];
  const verificationChecked = filterList[verificationStatusColumnIndex].length > 0;
  updatedList[verificationStatusColumnIndex] = [];

  const uniqueFoodDonors = uniqBy(options.customFilters.donations, 'location_id').sort((a, b) =>
    sortAlphabetically('dsc', a.location, b.location)
  );
  const rescuesWithSelectedFoodDonor = (options.customFilters.donations || []).filter(
    (rescue) => rescue.location === getDonationFoodDonorType()
  );
  const uniquePickupLocations = uniqBy(rescuesWithSelectedFoodDonor, 'pickup_location_id');

  return (
    <>
      <Box pl={2} pb={1}>
        <Grid container direction="row" justifyContent="flex-start" alignItems="center" spacing={2}>
          {options.customFilters.displayFoodDonorFilters && (
            <>
              <Grid item>
                <FormControl variant="outlined" size="small" style={{ minWidth: 140 }}>
                  <InputLabel shrink>Food Donor</InputLabel>
                  <Select
                    label="Food Donor"
                    value={getDonationFoodDonorType()}
                    onChange={(event) => {
                      onFoodDonorFilterChange(event.target.value);
                    }}
                  >
                    <MenuItem value="All" data-name="all">
                      All
                    </MenuItem>
                    {uniqueFoodDonors.map((item) => (
                      <MenuItem key={item.location_id} value={item.location}>
                        {item.location}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item>
                <Tooltip title={getDonationFoodDonorType() === 'All' ? 'Please select a Food Donor first.' : ''}>
                  <FormControl variant="outlined" size="small" style={{ minWidth: 140 }}>
                    <InputLabel shrink>Pickup Location</InputLabel>
                    <Select
                      label="Pickup Location"
                      disabled={getDonationFoodDonorType() === 'All'}
                      value={getDonationPickupLocationType()}
                      onChange={(event) => {
                        onPickupLocationFilterChange(event.target.value);
                      }}
                    >
                      <MenuItem value="All" data-name="all">
                        All
                      </MenuItem>
                      {uniquePickupLocations.map((item) => (
                        <MenuItem key={item.pickup_location_id} value={renderDonationPickupLocationNameOrAdress(item)}>
                          {renderDonationPickupLocationNameOrAdress(item)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Tooltip>
              </Grid>
            </>
          )}

          {options.customFilters && options.customFilters.showPendingRegistrationFilter && (
            <Grid item>
              <FormControlLabel
                control={
                  <Checkbox
                    disabled={editMode}
                    checked={verificationChecked}
                    color="secondary"
                    onChange={() => {
                      filterUpdate(
                        verificationStatusColumnIndex,
                        'pending',
                        verificationStatusColumn.name,
                        verificationStatusColumn.filterType
                      );
                    }}
                  />
                }
                label="Pending Request(s)"
              />
            </Grid>
          )}
        </Grid>
      </Box>
      <Box display="flex" flexDirection="row">
        <TableFilterList {...restProps} filterList={updatedList} filterUpdate={filterUpdate} />
      </Box>
    </>
  );
};

const customEditFooter = ({ handleEditClick, handleReviewClick, reviewButtonEnabled }) => (
  <TableFooter>
    <TableRow>
      <TableCell
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          flexWrap: 'nowrap',
          marginRight: '24px',
          minHeight: '64px',
        }}
      >
        <Typography variant="body2" style={{ marginRight: '1rem' }}>
          Exit Edit Mode to select a different page
        </Typography>
        <Button onClick={handleEditClick}>Exit Edit Mode</Button>
        <Button
          disabled={!reviewButtonEnabled}
          onClick={handleReviewClick}
          style={{ marginLeft: '1rem' }}
          variant="contained"
          color="primary"
        >
          Review and Save
        </Button>
      </TableCell>
    </TableRow>
  </TableFooter>
);

const DonationsTable = ({
  donations,
  tableId,
  onLocationClick,
  onEditClick,
  onReviewClick,
  customToolbar = () => {},
  customFilters = {
    showPendingRegistrationFilter: false,
  },
  editedDonations,
  setEditedDonations,
  handleReviewClick,
  editMode,
  setEditMode,
}) => {
  const activeUser = useActiveUser();
  const activeSite = useActiveSite();
  const dispatch = useDispatch();
  const rescueSizes = useSelector((state) => state.entities.rescueSizes);
  const lbsPerMeal = useSystemSettings(LBS_PER_MEAL);
  const groups = useSelector(state => state.entities.groups);
  const donorLocations = useSelector(state => state.entities.sites.donorsLocations);
  const siteGroupIds = groups.bySiteId[activeSite.id] || [];
  const groupsList = siteGroupIds.map(id => groups.byId[id]).filter(Boolean);
  const availableGroups = groupsList.map((group) => group.name);
  const rescueSizesArray = rescueSizes.sorted;
  const isAdmin = hasAnyRoleInCurrentlySelectedSite(activeUser, [
    Roles.Admin,
    Roles.NationalSiteDirector,
    Roles.SiteDirector,
    Roles.SiteCoordinator,
  ]);
  const showEditRescueButton = isAdmin;

  useEffect(() => {
    if (isAdmin) {
      dispatch(fetchSiteGroupsIfNeeded(activeSite.id));
      dispatch(fetchSiteDonorsLocationsIfNeeded(activeSite.id));
    }
  }, [isAdmin]);

  const handleEditClick = () => {
    if (!showEditRescueButton) {
      setEditMode(false);
      return setEditedDonations([]);
    }

    if (editedDonations?.length > 0) {
      return confirmAlert({
        message: 'Are you sure you want to exit without saving?',
        buttons: [
          {
            label: 'No',
            color: 'primary',
          },
          {
            label: 'Yes',
            color: 'primary',
            onClick: () => {
              setEditMode(!editMode);
              setEditedDonations([]);
            },
          },
        ],
        customUI: ({ message, onClose, buttons }) => (
          <ConfirmationDialog buttons={buttons} closeDialog={onClose} message={message} />
        ),
      });
    }

    setEditMode(!editMode);
    return setEditedDonations([]);
  };

  const onEditableFieldChange = (donationEdited, donation, data) => {
    if (!donationEdited) {
      return setEditedDonations([...editedDonations, { id: donation.id, ...data }]);
    }

    return setEditedDonations(editedDonations.map((d) => (d.id !== donation.id ? d : { ...donationEdited, ...data })));
  };

  const handleUndoClick = (donation, field, previousWeight = 0) => {
    const editedRescuesReduced = editedDonations.reduce((acc, curr) => {
      if (curr.id === donation.id) {
        let reducedDonation = omit(curr, [field]);

        if (field === 'status') {
          reducedDonation = omit(curr, ['pause_date', 'resume_date', [field]]);
        }

        if (field === 'lbs') {
          reducedDonation = omit(curr, ['estimated', [field]]);
        }

        if (Object.keys(reducedDonation).length === 1) {
          return acc;
        }

        acc.push(reducedDonation);

        return acc;
      };
      acc.push(curr);
      return acc;
    }, []);

    // Reset value for uncontrolled lbs input
    if (field === 'lbs') {
      const currentLbsInput = document.getElementById(donation.id);
      currentLbsInput.value = previousWeight;
    }

    setEditedDonations(editedRescuesReduced);
  };

  const renderStatusTooltip = (donation) => {
    if (donation?.isRequest) {
      return 'Cannot edit pending requests.';
    }

    if (donation?.frequency === 0) {
      return 'Cannot pause one time only donations.';
    }

    return '';
  };

  const pickupLocationCustomBodyRender = (value, tableMeta) => {
    const donation = donations[getMuiTableDataIndex(tableMeta)];

    if (!donation) {
      return false;
    }

    const donationPickupLocationFullName = renderDonationPickupLocationNameOrAdress(donation);

    return donationPickupLocationFullName;
  };

  const frequencyCustomBodyRender = donation => {
    switch (donation.frequency) {
      case 0:
        return 'one-time';
      case 1:
        return donation.frequency_every === 1 ? 'weekly' : 'every other week';
      case 2:
        return 'monthly';
      default:
        return 'unknown';
    }
  };

  const startDateCustomBodyRender = value => (value ? moment(value, 'YYYY-MM-DD').format('MM/DD/YYYY') : null);

  const verificationStatusBodyRender = value => value && value !== 'approved' ? _.startCase(_.toLower(value)) : '-';

  const weightCustomBodyRender = (value, tableMeta) => {
    const donation = donations[getMuiTableDataIndex(tableMeta)];
    const rescueSize = rescueSizesArray.find((size) => size.id === donation?.rescue_size_id);
    const estimatedLBSValue = getRescueEstimatedLbs(rescueSize, lbsPerMeal);
    const donationWeight = value !== null ? value : estimatedLBSValue;
    if (editMode) {
      const donationEdited = editedDonations.find((d) => d.id === donation.id);

      return (
        <Box display="flex">
          <Tooltip title={donation.isRequest ? 'Cannot edit pending requests.' : ''}>
            <span>
              <TextField
                id={`${donation.id}`}
                sx={
                  donationEdited && 'lbs' in donationEdited
                    ? {
                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          border: `3px solid ${Colors.bulkEdit.main}`,
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: `2px solid ${Colors.bulkEdit.main}`,
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          border: `3px solid ${Colors.bulkEdit.main}`,
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: Colors.bulkEdit.main,
                        },
                        marginRight: '1rem',
                        backgroundColor: 'white',
                      }
                    : { backgroundColor: 'white' }
                }
                style={{ width: 100 }}
                disabled={donation.isRequest}
                type="number"
                variant="outlined"
                size="small"
                defaultValue={donationEdited && 'lbs' in donationEdited ? donationEdited.lbs : donationWeight}
                InputProps={{
                  inputProps: { min: 0 },
                }}
                onBlur={(event) => {
                  onEditableFieldChange(donationEdited, donation, {
                    lbs: event.target.value,
                    estimated: donationWeight,
                  });
                }}
              />
            </span>
          </Tooltip>
          {donationEdited && 'lbs' in donationEdited && (
            <UndoButton handleClick={() => handleUndoClick(donationEdited, 'lbs', donationWeight)} />
          )}
        </Box>
      );
    }

    return donationWeight !== null && donationWeight !== undefined ? donationWeight : '-';
  };

  const statusCustomBodyRender = (value, tableMeta, csv = false) => {
    if (csv) {
      return value || 'N/A';
    }
    
    if (editMode) {
      const donation = donations[getMuiTableDataIndex(tableMeta)];
      const donationEdited = editedDonations.find((d) => d.id === donation.id);
      const isPausedByDonorLink = isDonationPausedByDonorLink(donation, donorLocations.byId[donation.location_id]);

      return (
        <Box display="flex">
          {(!donationEdited && donation.status !== 'PAUSED') ||
          (donationEdited && donationEdited.status !== 'PAUSED') ? (
            <Tooltip title={renderStatusTooltip(donation)}>
              <span>
                <Button
                  disabled={donation.frequency === 0 || donation.isRequest}
                  color="secondary"
                  startIcon={<PauseCircleOutlineIcon />}
                  variant="contained"
                  onClick={() => {
                    onEditableFieldChange(donationEdited, donation, {
                      status: 'PAUSED',
                      pause_date: (moment().isBefore(donation.start_date) ? moment(donation.start_date) : moment()).format(
                        'YYYY-MM-DD'
                      ),
                    });
                  }}
                >
                  Pause now
                </Button>
              </span>
            </Tooltip>
          ) : (
            <Tooltip title={isPausedByDonorLink ? DONOR_LINK_PAUSED_TOOLTIP : ''}>
              <span>
                <Button
                  disabled={donation.frequency === 0 || isPausedByDonorLink}
                  color="secondary"
                  startIcon={<PlayCircleOutlineIcon />}
                  variant="contained"
                  onClick={() => {
                    onEditableFieldChange(donationEdited, donation, {
                      status: 'CURRENT',
                      resume_date: (moment().isBefore(donation.pause_date) ? moment(donation.pause_date) : moment()).format(
                        'YYYY-MM-DD'
                      ),
                    });
                  }}
                >
                  Resume now
                </Button>
              </span>
            </Tooltip>
          )}
          {donationEdited && 'status' in donationEdited && (
            <UndoButton handleClick={() => handleUndoClick(donationEdited, 'status', donation)} />
          )}
        </Box>
      )
    }
    return value || 'N/A';
  };

  const donorLinkCustomBodyRender = (value, csv = false) => {
    if (csv) {
      return value ? 'Donor Link' : '';
    }

    if (value) {
      return (
        <Box display="flex" justifyContent="center">
          <Tooltip arrow placement="top" title="This donor uses Donor Link">
            <span>
              <DonorLinkIcon size={24} />
            </span>
          </Tooltip>
        </Box>
      );
    }

    return '-';
  };

  const groupsCustomBodyRender = (value) => {
    if (!value) {
      return '-';
    }

    const parsed = JSON.parse(value);

    if (Array.isArray(parsed)) {
      const groupNames = parsed.map((group) => group.name);

      if (groupNames.length) {
        return groupNames.join(', ');
      }

      return '-';
    }

    return '-';
  };

  return (
    <BaseMUIDataTable
      tableId={tableId}
      title={editMode ? 'Edit Mode' : 'Rescues'}
      data={donations}
      columns={[
        {
          name: 'id',
          label: 'ID',
          options: {
            filter: false,
            sort: false,
            display: false,
          },
        },
        {
          name: 'location',
          label: 'Food Donor',
          options: {
            setCellProps: () => ({
              style: {
                cursor: 'pointer',
              },
            }),
            // the link sets "value" to the full name, keep the filter on the plain food donor name
            filterOptions: foodDonorNameFilterOptions(donations, getRescuePickupLocationFullName),
            customBodyRender: (value, tableMeta) => {
              const donation = donations[getMuiTableDataIndex(tableMeta)];
              const foodDonorName = getRescuePickupLocationFullName(donation);

              return (
                <Link
                  onClick={() => onLocationClick(donation.donor_id, donation.location_id)}
                  underline="hover"
                  // value set for search to work
                  value={foodDonorName}
                >
                  {foodDonorName}
                </Link>
              );
            },
          },
        },
        {
          name: 'pickup_location_name',
          label: 'Pickup Location',
          options: {
            customBodyRender: (value, tableMeta) => pickupLocationCustomBodyRender(value, tableMeta),
            customBodyRenderCSV: (value, tableMeta) => pickupLocationCustomBodyRender(value, tableMeta),
            customFilterListOptions: {
              render: value => `Pickup Location: ${value}`,
            },
            filter: false,
            display: false,
          },
        },
        {
          name: 'donation',
          label: 'Description',
        },
        {
          name: 'frequency',
          label: 'Frequency',
          options: {
            customBodyRender: (value, tableMeta) => {
              const donation = donations[getMuiTableDataIndex(tableMeta)];

              return frequencyCustomBodyRender(donation);
            },
            customBodyRenderCSV: (value, tableMeta) => {
              const donation = donations[getMuiTableDataIndex(tableMeta)];

              return frequencyCustomBodyRender(donation);
            },
          },
        },
        {
          name: 'display_days',
          label: 'Day(s)',
          options: {
            customBodyRender: value => value || '-',
          },
        },
        {
          name: 'start_date',
          label: 'Start Date',
          options: {
            sort: true,
            customBodyRender: value => startDateCustomBodyRender(value),
            customBodyRenderCSV: value => startDateCustomBodyRender(value),
          },
        },
        {
          name: 'verification_status',
          label: 'Verification status',
          options: {
            filter: false,
            display: false,
            filterType: 'checkbox',
            customBodyRender: value => verificationStatusBodyRender(value),
            filterOptions: {
              logic: (status, filters) => {
                if (!filters || filters.length === 0) {
                  return false;
                }

                return status !== 'Pending';
              },
              names: ['Pending Request(s)'],
            },
          },
        },
        {
          name: 'lbs',
          label: 'Weight',
          options: {
            filter: true,
            display: true,
            customFilterListOptions: {
              render: (value) => `Weight (lbs): ${value}`,
            },
            customBodyRender: (value, tableMeta) => weightCustomBodyRender(value, tableMeta),
            customBodyRenderCSV: (value, tableMeta) => {
              const donation = donations[getMuiTableDataIndex(tableMeta)];
              const rescueSize = rescueSizesArray.find((size) => size.id === donation?.rescue_size_id);
              const estimatedLBSValue = getRescueEstimatedLbs(rescueSize, lbsPerMeal);
              const donationWeight = value !== null ? value : estimatedLBSValue;
              return donationWeight !== null && donationWeight !== undefined ? donationWeight : '-';
            },
          },
        },
        {
          name: 'status',
          label: 'Status',
          options: {
            filter: true,
            display: true,
            customFilterListOptions: {
              render: value => `Status: ${value}`,
            },
            customBodyRender: (value, tableMeta) => statusCustomBodyRender(value, tableMeta),
            customBodyRenderCSV: (value, tableMeta) => statusCustomBodyRender(value, tableMeta, true),
          },
        },
        ...(isAdmin
          ? [
              {
                name: 'donation_groups',
                label: 'Group(s)',
                options: {
                  filter: true,
                  customFilterListOptions: {
                    render: (value) => `Group: ${value.replace('_', '')}`,
                  },
                  customBodyRender: (value) => groupsCustomBodyRender(value),
                  customBodyRenderCSV: (value) => groupsCustomBodyRender(value),
                  sortFilterList: false,
                  filterOptions: {
                    // Add underscore prefix to display as a first option (filter options are sorted internally)
                    names: ['_None', ...availableGroups],
                    renderValue: (val) => val.replace('_', ''),
                    logic(value, filterVal) {
                      if (!filterVal || filterVal.length === 0) {
                        return false;
                      }

                      if (filterVal.includes('_None')) {
                        return value !== '-';
                      }
                      return !filterVal.some((group) => value.includes(group));
                    },
                  },
                },
              },
            ]
          : []),
        {
          name: 'donor_link_donation',
          label: 'Donor Link',
          options: {
            filter: true,
            display: true,
            customFilterListOptions: {
              render: value => `${value}`,
            },
            filterOptions: {
              names: ['Donor Link Inactive', 'Donor Link Active'],
              logic: (option, filters) => {
                if (!filters || filters.length === 0) {
                  return false;
                }

                if (option === '-') {
                  return !filters.includes('Donor Link Inactive');
                }

                if (option) {
                  return !filters.includes('Donor Link Active');
                }

                return true;
              },
            },
            customBodyRender: (value, tableMeta) => donorLinkCustomBodyRender(value),
            customBodyRenderCSV: (value, tableMeta) => donorLinkCustomBodyRender(value, true),
          },
        },
        {
          name: 'actions',
          label: 'Actions',
          options: {
            download: false,
            filter: false,
            sort: false,
            display: !editMode,
            forceDisplay: true,
            searchable: false,
            viewColumns: false,
            customBodyRender: (value, tableMeta) => {
              const donation = donations[getMuiTableDataIndex(tableMeta)];

              if (!donation) {
                return null;
              }

              const isVerificationStatusPending = donation.verification_status === 'pending'
                || donation.status === 'pending';

              return (
                <TableActionsButtonsWrapper>
                  {onEditClick && !isVerificationStatusPending && (
                    <TableActionButton
                      title="Edit Rescue"
                      onClick={() => onEditClick(donations[getMuiTableDataIndex(tableMeta)].id)}
                    />
                  )}

                  {onReviewClick && isVerificationStatusPending && (
                    <TableActionButton
                      icon={VisibilityIcon}
                      title="Review Rescue"
                      onClick={() => onReviewClick(donations[getMuiTableDataIndex(tableMeta)].id)}
                    />
                  )}
                </TableActionsButtonsWrapper>
              );
            },
          },
        },
      ]}
      options={{
        customToolbar: () =>
          customToolbar({
            handleEditClick,
            editMode,
            showEditRescueButton,
            handleReviewClick,
            reviewButtonEnabled: editedDonations?.length,
          }),
        customFooter: editMode
          ? () => customEditFooter({ handleEditClick, handleReviewClick, reviewButtonEnabled: editedDonations.length })
          : null,
        selectableRows: 'none',
        responsive: 'simple',
        downloadOptions: {
          filename: activeSite?.name ? `${activeSite.name} Rescues.csv` : formatCsvFileName('Rescues'),
        },
        customFilters: customFilters,
        download: !editMode,
        filter: !editMode,
        search: !editMode,
        viewColumns: !editMode,
        sort: !editMode,
        setRowProps: (row, dataIndex) => {
          const donation = donations[dataIndex];
          const rescueEdited = (editedDonations || []).find((d) => d.id === donation.id);

          return {
            className: rescueEdited ? 'editedRow' : '',
          };
        },
      }}
      components={{
        TableFilterList: (props) => CustomFilterList(props, editMode),
      }}
    />
  );
};

export default DonationsTable;
